import { onBeforeUnmount, ref, shallowRef } from 'vue';

import { loadStripe } from '@stripe/stripe-js';

import type { Stripe, StripeElements, StripePaymentElement, StripeError } from '@stripe/stripe-js';

// Stripe Payment Element lifecycle wrapper.
//
// Runs in the host JS realm on purpose: wujie's sandbox splits JS / DOM
// (JS in a sandbox iframe, DOM in the host shadow root), and Stripe
// Element iframes inherit `window.parent` from the DOM tree — so a
// sandbox-hosted Stripe.js never receives the ready-handshake message
// and the form never renders. Keeping this composable in host code
// puts JS and DOM in the same realm.
//
// Lifecycle: `attach(container, payload)` → `confirm()` → `detach()`.
// Each call to `attach` builds a fresh `elements` instance against the
// supplied `clientSecret`; we do not cache across sessions because the
// setup intent rotates per-edit.

// Read env lazily (inside attach) — allows tests to `vi.stubEnv` after
// the module loads, and future runtime rotations to work without a
// module reload.
function publishableKey(): string | undefined {
  return import.meta.env.VITE_STRIPE_PUBLISHABLEKEY as string | undefined;
}

const FALLBACK_READY_MS = 5000;

type Status = 'idle' | 'loading' | 'ready' | 'error';

export function useStripeElements() {
  const status = ref<Status>('idle');
  const error = ref<StripeError | Error | null>(null);
  const stripe = shallowRef<Stripe | null>(null);
  const elements = shallowRef<StripeElements | null>(null);
  const paymentElement = shallowRef<StripePaymentElement | null>(null);
  // Submit lock survives a destroyed Stripe instance — a click during the
  // tail of confirmSetup() must not trigger a second confirm.
  const submitting = ref(false);

  let fallbackReadyTimer: ReturnType<typeof setTimeout> | null = null;
  // Cancel scope for the in-flight attach. Any await inside attach()
  // checks `signal.aborted` before touching shared state, so a
  // close+reopen while `loadStripe()` is pending can't repopulate
  // `elements` / `paymentElement` with the previous clientSecret.
  let attachAbort: AbortController | null = null;

  function clearFallbackTimer() {
    if (fallbackReadyTimer !== null) {
      clearTimeout(fallbackReadyTimer);
      fallbackReadyTimer = null;
    }
  }

  async function attach(container: HTMLElement, clientSecret: string): Promise<void> {
    const key = publishableKey();
    if (!key) {
      status.value = 'error';
      error.value = new Error('VITE_STRIPE_PUBLISHABLEKEY missing');
      return;
    }
    // Supersede any prior attach still running.
    attachAbort?.abort();
    attachAbort = new AbortController();
    const { signal } = attachAbort;
    status.value = 'loading';
    error.value = null;

    if (!stripe.value) {
      try {
        stripe.value = await loadStripe(key);
      } catch (e) {
        if (signal.aborted) return;
        status.value = 'error';
        error.value = e instanceof Error ? e : new Error(String(e));
        return;
      }
    }
    if (signal.aborted) return;
    if (!stripe.value) {
      status.value = 'error';
      error.value = new Error('loadStripe returned null (ad-blocker / CSP / offline?)');
      return;
    }

    const nextElements = stripe.value.elements({
      locale: 'en',
      clientSecret,
      appearance: {
        variables: {
          fontSizeBase: '14px',
        },
      },
    });
    const nextPayment = nextElements.create('payment', {
      business: { name: 'PhoenixAI Inc.' },
    });
    nextPayment.on('ready', () => {
      if (signal.aborted) return;
      clearFallbackTimer();
      status.value = 'ready';
    });
    nextPayment.on('loaderror', (ev) => {
      if (signal.aborted) return;
      clearFallbackTimer();
      status.value = 'error';
      error.value = ev.error;
    });
    elements.value = nextElements;
    paymentElement.value = nextPayment;
    nextPayment.mount(container);
    // Fallback if `ready` never arrives (ad-blocker / CSP / script load
    // failure) — otherwise the confirm button would stay disabled
    // forever and the user can't retry.
    fallbackReadyTimer = setTimeout(() => {
      if (signal.aborted) return;
      if (status.value === 'loading') status.value = 'ready';
    }, FALLBACK_READY_MS);
  }

  async function confirm(): Promise<{ ok: true } | { ok: false; error: StripeError | Error }> {
    if (submitting.value) return { ok: false, error: new Error('Already submitting') };
    if (!stripe.value || !elements.value) {
      return { ok: false, error: new Error('Stripe Elements not attached') };
    }
    submitting.value = true;
    try {
      const { error: stripeError } = await stripe.value.confirmSetup({
        elements: elements.value,
        redirect: 'if_required',
      });
      if (stripeError) return { ok: false, error: stripeError };
      return { ok: true };
    } finally {
      submitting.value = false;
    }
  }

  function detach(): void {
    // Signal the in-flight attach so its `await loadStripe()` or its
    // `ready`/`loaderror` handlers take their early return post-abort
    // and don't repopulate elements / paymentElement / status with a
    // now-stale clientSecret.
    attachAbort?.abort();
    attachAbort = null;
    clearFallbackTimer();
    paymentElement.value?.destroy();
    paymentElement.value = null;
    elements.value = null;
    status.value = 'idle';
    error.value = null;
    // Keep `stripe.value` cached across detach/attach within the same page
    // session — loadStripe()'s internal memo would dedupe anyway, but
    // holding the resolved instance avoids the second-open await.
  }

  onBeforeUnmount(detach);

  return { status, error, submitting, attach, confirm, detach };
}
